import {Component, OnInit, NgZone, HostListener} from '@angular/core';
import {Router} from '@angular/router';
import {environment} from 'src/environments/environment';
import {TrustiiService} from 'src/app/services/trustii.service';
import {DataService, DatasetBannerModel} from 'src/app/services/data.service';
import {SessionService} from 'src/app/services/session.service';
import {DialogService} from 'src/app/services/dialog.service';
import {tap, filter, distinctUntilChanged} from 'rxjs/operators';

@Component({
  selector: 'app-enterprise-datasets',
  templateUrl: './enterprise-datasets.component.html',
  styleUrls: ['./enterprise-datasets.component.scss'],
})
export class EnterpriseDatasetsComponent implements OnInit {
  isReady = false;
  devMode = false;
  canUploadDataset = false;
  name = '';
  uploadInProgress = null;
  elements: any = [];
  innerHeight: any;
  messages = [];
  postMessageDatasetId = null;
  user: SessionService['user'] = null;
  meta: any = {
    total: 0,
    nb_page: 1,
    current_page: 1,
    first_page: 1,
    last_page: 1,
    count: 0,
    per_page: 20,
  };
  isDs = false;

  constructor(
    private router: Router,
    private trustiiService: TrustiiService,
    private dialogService: DialogService,
    public datasetBannerModel: DatasetBannerModel,
    public dataservice: DataService,
    private sessionService: SessionService,
    private zone: NgZone
  ) {}

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.innerHeight = window.innerHeight / 1.75;
  }

  /**
   * Init component
   */
  ngOnInit() {
    this.devMode = !environment.production;
    this.innerHeight = window.innerHeight / 1.75;
    this.canUploadDataset = this.sessionService.canUploadDataset();
    this.user = this.sessionService.getUser();
    this.dataservice.fileUploadProgressSubject.subscribe((data) => {
      this.zone.run(() => {
        this.uploadInProgress = this.dataservice.enterpriseDatasetUploadQueue;
      });
    });
    this.dataservice.enterpriseFileUploadSubject.subscribe((data) => {
      this.zone.run(() => {
        this.getDataSets();
        this.uploadInProgress = this.dataservice.enterpriseDatasetUploadQueue;
      });
    });
    this.dataservice.enterpriseModelGenerateSubject.subscribe((data) => {
      this.getDataSets();
    });
    this.getDataSets();
  }

  canAccessChallengeFunctionnality() {
    return this.sessionService.canAccessChallengeFunctionnality();
  }

  /**
   * Get datasets
   */
  getDataSets(): void {
    this.isReady = false;
    this.elements = [];
    this.trustiiService
      .getDatasets(this.name, this.meta.page, this.meta.per_page)
      .subscribe(
        (res) => {
          this.elements = res.data;
          this.meta = res.meta;
          this.isReady = true;
        },
        (err) => {
          this.isReady = true;
          this.elements = [];
        }
      );
  }

  /**
   * Upload a dataset
   */
  uploadDataset(): void {
    if (!this.canUploadDataset) {
      return;
    }
    this.dataservice.data = { downloadMetadata: false };
    this.router.navigateByUrl('enterprise/datasets/upload');
  }

  /**
   * Configure dataset
   *
   * @param datasetId Dataset ID
   */
  configureDataset(datasetId: number): void {
    this.trustiiService.getDataset(datasetId).subscribe(
      (res) => {
        if (res.data.status === 'in-progress') {
          this.configureDataset(datasetId);
          return;
        } else {
          this.dataservice.setData(res.data);
          this.router.navigateByUrl(
            'enterprise/datasets/' + datasetId + '/configuration'
          );
        }
      },
      (err) => {}
    );
  }

  /**
   * Show models
   *
   * @param datasetId Dataset ID
   */
  showModels(datasetId: number): void {
    const entry = this.dataservice.enterpriseModelGenerateQueue.find(
      (item) => item.datasetId === datasetId
    );
    if (entry && entry.status !== 'in-progress') {
      this.dataservice.enterpriseModelGenerateQueue =
        this.dataservice.enterpriseModelGenerateQueue.filter(
          (item) => item.datasetId !== datasetId
        );
    }

    this.dataservice.data.trainigData = datasetId;
    this.router.navigateByUrl('enterprise/datasets/' + datasetId + '/mlmodels');
  }

  /**
   * Generate preditctions
   *
   * @param options.datasetId Dataset ID
   */
  generatePredictions(options): void {
    options.that.dataservice.data.trainigData = options.datasetId;
    options.that.trustiiService.getMlModels('', options.datasetId).subscribe(
      (res) => {
        if (res.data.length > 0) {
          const modelWithBestAccuracy = res.data.sort(
            (a, b) => b.accuracy - a.accuracy
          )[0];
          options.that.dataservice.data.modelId = modelWithBestAccuracy.id;
          options.that.router.navigateByUrl(
            'enterprise/mlmodels/' +
              modelWithBestAccuracy.id +
              '/insights/generate'
          );
        } else {
          options.that.dialogService.openAlertDialog(
            'No valid model for the insight predictions',
            'ERROR'
          );
        }
      },
      (err) => {
        options.that.dialogService.openAlertDialog(
          'Failed to fetch models for the insight prediction. Please try again later.',
          'ERROR'
        );
      }
    );
  }

  /**
   * Update dataset
   *
   * @param index Index in elements array
   */
  updateDataset(index: number): void {
    const payload = {
      status:
        'configured' === this.elements[index].status ? 'hidden' : 'configured',
    };

    this.trustiiService
      .updateDataset(payload, this.elements[index].id)
      .subscribe(
        (res) => {
          this.elements[index].status = payload.status;
        },
        (err) => {}
      );
  }

  /**
   * Remove dataset
   *
   * @param index Index in elements array
   */
  removeDataset(index: number): void {
    this.dialogService
      .openConfirmDialog(
        'Do you really want to remove the file of this dataset ?\nThis action is irreversible.'
      )
      .pipe(
        distinctUntilChanged(),
        filter((result) => result && result.data === 'confirmation'),
        tap(() => this.removeDatasetRequest(index))
      )
      .subscribe();
  }

  removeDatasetRequest(index: number): void {
    this.trustiiService.deleteDataset(this.elements[index].id).subscribe(
      (res) => {
        this.elements[index].status = 'removed';
      },
      (err) => {}
    );
  }

  /**
   * When limit changed
   *
   * @param perPage Nb result per page
   */
  onLimitChanged(perPage: number): void {
    this.meta.per_page = perPage;
    this.getDataSets();
  }

  /**
   * When page changed
   *
   * @param page The page
   */
  onPageChanged(page: number): void {
    this.meta.page = page;
    this.getDataSets();
  }

  /**
   * Get days
   *
   * @param stringDate The date
   * @return days ago difference
   */
  getDays(createdDate: string): string {
    const difference = new Date().getTime() - new Date(createdDate).getTime();
    const day = Math.round(Math.abs(difference / (1000 * 60 * 60 * 24)));
    if (day === 0) {
      return ' today';
    }

    return day > 1 ? day.toString() + ' days ago' : day.toString() + ' day ago';
  }

  modelGenerationInProgressForDataset(datasetId) {
    return this.dataservice.enterpriseModelGenerateQueue.find(
      (item) => item.datasetId === datasetId
    )
      ? this.dataservice.enterpriseModelGenerateQueue.find(
          (item) => item.datasetId === datasetId
        )
      : null;
  }

  /**
   * Get Working Model Counts
   * @ mlModels
   */
  getWorkingModelCount(mlModels: []): number {
    let count = 0;
    mlModels.forEach((element) => {
      if (element['status'] === 'done') {
        count++;
      }
    });
    return count;
  }

  /**
   * Cancel upload in progress
   */
  cancelUpload(uploadInprogressId: number) {
    this.dataservice.enterpriseDatasetUploadQueue
      .find((item) => item.id === uploadInprogressId)
      .uploadRequest.unsubscribe();
    this.dataservice.enterpriseDatasetUploadQueue =
      this.dataservice.enterpriseDatasetUploadQueue.filter(
        (item) => item.id !== uploadInprogressId
      );
    this.zone.run(() => {
      this.uploadInProgress = this.dataservice.enterpriseDatasetUploadQueue;
    });
  }

  showMlModelError(error: string) {
    if (!error) {
      return;
    }
    this.dialogService.openAlertDialog(
      '350px',
      'ERROR DURING MODEL GENERATION',
      'ERROR',
      'Ok',
      'modal-warning'
    );
  }

  getDatasetImage(dataset: any): string {
    return dataset.image === null
      ? '/assets/img/svg/dataset.svg'
      : 'data:image/png;base64,' + dataset.image;
  }

  openDatasetPortal(datasetObject) {
    Object.assign(this.datasetBannerModel.dataset, datasetObject);
    Object.assign(this.dataservice.data, datasetObject);
    this.router.navigateByUrl('datasets/' + datasetObject.id);
  }

  gotoTerms() {
    this.router.navigateByUrl(this.isDs ? 'terms' : 'terms-organisation');
  }
}
